import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { useInfiniteScroll } from '@/components/ui/data-grid/use-infinite-scroll';
import {
    Drawer,
    DrawerClose,
    DrawerContent,
    DrawerDescription,
    DrawerHeader,
    DrawerTitle,
} from '@/components/ui/drawer';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Spinner } from '@/components/ui/spinner';
import { Tabs, TabsContent } from '@/components/ui/tabs';

import {
    useMarkAllNotificationsRead,
    useMarkNotificationRead,
    useUnreadCounts,
} from '../hooks/useNotifications';
import { useNotificationsListInfinite } from '../hooks/useNotificationsListInfinite';
import { NOTIFICATION_TABS, type INotification, type NotificationType } from '../types';
import { NotificationEmptyState } from './NotificationEmptyState';
import { NotificationIcon } from './NotificationIcon';
import { NotificationRow } from './NotificationRow';
import { NotificationTabsList } from './NotificationTabsList';

interface InfiniteListProps {
    type: NotificationType;
    enabled: boolean;
    onMarkRead: (id: string) => void;
    onNavigate: (notification: INotification) => void;
}

function InfiniteNotificationList({ type, enabled, onMarkRead, onNavigate }: InfiniteListProps) {
    const { items, isLoading, hasNextPage, isFetchingNextPage, fetchNextPage } =
        useNotificationsListInfinite(type, enabled);
    const sentinelRef = useInfiniteScroll({
        hasNextPage,
        isFetchingNextPage,
        onFetchNextPage: fetchNextPage,
    });

    if (isLoading) {
        return (
            <div className="flex h-full items-center justify-center">
                <Spinner />
            </div>
        );
    }

    if (items.length === 0) {
        return <NotificationEmptyState type={type} className="h-full" />;
    }

    return (
        <ScrollArea className="h-full">
            <div className="divide-y divide-border-default">
                {items.map((n) => (
                    <NotificationRow
                        key={n.id}
                        notification={n}
                        onMarkRead={onMarkRead}
                        onNavigate={onNavigate}
                    />
                ))}
                <div ref={sentinelRef} className="flex h-10 items-center justify-center">
                    {isFetchingNextPage && <Spinner />}
                </div>
            </div>
        </ScrollArea>
    );
}

interface NotificationsViewAllDrawerProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    tab: NotificationType;
    onTabChange: (tab: NotificationType) => void;
}

/**
 * Right-side drawer showing ALL notifications with the same Messages/Alerts
 * tabs as the popover, using infinite scroll. Opened from the popover's
 * "View all" footer (seeded with the currently-active tab).
 */
export function NotificationsViewAllDrawer({
    open,
    onOpenChange,
    tab,
    onTabChange,
}: NotificationsViewAllDrawerProps) {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const { data: counts } = useUnreadCounts();
    const markRead = useMarkNotificationRead();
    const markAll = useMarkAllNotificationsRead();

    const messageUnread = counts?.messageUnread ?? 0;
    const alertUnread = counts?.alertUnread ?? 0;

    const handleNavigate = (n: INotification) => {
        if (!n.isRead) markRead.mutate(n.id);
        if (n.actionUrl) {
            onOpenChange(false);
            navigate(n.actionUrl);
        }
    };

    return (
        <Drawer open={open} onOpenChange={onOpenChange} direction="right">
            <DrawerContent className="flex w-full flex-col gap-0 p-0 sm:max-w-[460px]">
                <DrawerHeader className="flex-row items-center justify-between gap-2 border-b border-border-default p-4">
                    <div className="space-y-0.5">
                        <DrawerTitle className="text-base">
                            {t('notifications.title', 'Notifications')}
                        </DrawerTitle>
                        <DrawerDescription className="sr-only">
                            {t('notifications.allNotifications', 'All notifications')}
                        </DrawerDescription>
                    </div>
                    <div className="flex items-center gap-1">
                        <Button
                            type="button"
                            variant="ghost"
                            size="xs"
                            className="gap-1 text-xs text-muted-foreground"
                            onClick={() => markAll.mutate(tab)}
                            disabled={markAll.isPending}
                        >
                            <NotificationIcon name="CheckCheck" className="size-3.5" />
                            {t('notifications.markAllRead', 'Mark all as read')}
                        </Button>
                        <DrawerClose asChild>
                            <Button
                                type="button"
                                variant="ghost"
                                size="icon-sm"
                                aria-label={t('common.close', 'Close')}
                            >
                                <NotificationIcon name="X" className="size-4" />
                            </Button>
                        </DrawerClose>
                    </div>
                </DrawerHeader>

                <Tabs
                    value={tab}
                    onValueChange={(v) => onTabChange(v as NotificationType)}
                    className="flex min-h-0 flex-1 flex-col gap-0"
                >
                    <div className="border-b border-border-default p-2">
                        <NotificationTabsList
                            messageUnread={messageUnread}
                            alertUnread={alertUnread}
                        />
                    </div>
                    {NOTIFICATION_TABS.map((value) => (
                        <TabsContent key={value} value={value} className="mt-0 min-h-0 flex-1">
                            <InfiniteNotificationList
                                type={value}
                                enabled={open && tab === value}
                                onMarkRead={(id) => markRead.mutate(id)}
                                onNavigate={handleNavigate}
                            />
                        </TabsContent>
                    ))}
                </Tabs>
            </DrawerContent>
        </Drawer>
    );
}
